import { get, isEmpty, isUndefined, omitBy } from 'lodash';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';

import { getFeatureFlags } from '@utils/feature-flags';
import { assetQuery, categoriesQuery, floodlightQuery, seomaticQuery } from '../../content/query-snippets';
import { categoriesPropType, floodlightPageLoadEventPropTypes, recipesPropType } from '../../prop-types';
import { ContentMatrix } from '../../components/content-matrix';
import { extractSeoMetaFromEntry, seoPropDefaults, seoPropTypes } from '../../utils/extract-seo-meta-from-entry';
import { fetch } from '../../content/queries';
import { FilterGrid } from '../../components/filter-grid';
import { Layout } from '../../components/layout';
import { logger } from '../../utils/logger';
import { navigation } from '../../constants';
import { NewsCard } from '../../components/card';
import { PageHeader } from '../../components/page-header';
import { setCacheHeaders } from '../../utils/set-cache-headers';
import Error from '../_error';

const DEFAULT_FILTER = 'All News';

const imageDimensions = { width: '396px', height: '505px' };

const NewsPage = ({ contentMatrix, filters, metaContent, news, pageTitle, statusCode, floodlightPageLoadEvent }) => {
  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }
  return (
    <Layout title={pageTitle} metaContent={metaContent} floodlightPageLoadEvent={floodlightPageLoadEvent}>
      <PageHeader
        heading={pageTitle}
        description="Knowledge is power. So is PERi-PERi. Get stuck into PERi-Perks events, music, art, menu updates and more…"
        eyebrow="What’s happening"
        eyebrowBackgroundColor="primaryRed"
        eyebrowTextColor="white"
      />
      <FilterGrid
        cardComponent={NewsCard}
        defaultFilter={DEFAULT_FILTER}
        items={news}
        {...{ filters, imageDimensions }}
      />
      <ContentMatrix hasExternalHeader lastBlockBottomPadding={false} blocks={contentMatrix} />
    </Layout>
  );
};

NewsPage.getInitialProps = async ({ query, res }) => {
  const preview = get(query, 'x-craft-live-preview');
  const token = get(query, 'token');
  const slug = 'news-and-events';
  const featureFlags = await getFeatureFlags();

  const contentQuery = gql`
    query getContentFromCMS {
      content: entries(siteId: "${process.env.cms.siteId}", slug: "${slug}") {
        title
        ... on pages_Entry {
          ${ContentMatrix.query}
          ${floodlightQuery}
        }
      }
      news: entries(section: "news", siteId: "${process.env.cms.siteId}") {
        ... on news_Entry {
          date
          image: ${assetQuery('gridImage')}
          newsCategory {
            title
          }
          slug
          title
        }
      }
      categories: ${categoriesQuery('newsCategories')}
      ${seomaticQuery(slug)}
    }
  `;

  try {
    const data = await fetch({
      query: contentQuery,
      headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
    });

    if (res) {
      if (isEmpty(data.news) || isEmpty(data.content)) {
        // eslint-disable-next-line no-param-reassign
        res.statusCode = 404;
        return { statusCode: 404, featureFlags };
      }

      const isPreview = preview && token;
      setCacheHeaders({ res, isPreview });
    }

    const categories = get(data, 'categories', []);
    const contentMatrix = get(data, 'content[0].contentMatrix', []);
    const entries = get(data, 'news', []);
    const pageTitle = get(data, 'content[0].title', []);
    const floodlightPageLoadEvent = get(data, 'content.floodlightPageLoadEvent[0]');
    const metaContent = extractSeoMetaFromEntry(data);

    const news = entries.map(({ newsCategory, title, ...rest }) => ({
      category: get(newsCategory, '[0].title'),
      heading: title,
      href: `${navigation.NEWS}/${rest.slug}`,
      ...rest,
    }));

    const mappedCategories = categories.map(({ title }) => title);

    const filters = [DEFAULT_FILTER, ...mappedCategories];

    return {
      contentMatrix,
      filters,
      metaContent,
      news,
      pageTitle,
      floodlightPageLoadEvent,
      featureFlags,
    };
  } catch (err) {
    logger.error('CMS: Failed to fetch news from CMS', err);
    return { statusCode: 500, featureFlags };
  }
};

NewsPage.propTypes = {
  contentMatrix: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filters: categoriesPropType.isRequired,
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: PropTypes.shape(seoPropTypes),
  news: recipesPropType.isRequired,
  pageTitle: PropTypes.string.isRequired,
  statusCode: PropTypes.number,
};

NewsPage.defaultProps = {
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
  statusCode: null,
};

export default NewsPage;
